import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUiPreferences } from '../../contexts/UiPreferencesContext';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { useTab } from '../../TabContext';
import ShortcutHelpPopover from '../ShortcutHelpPopover';
import { ThemeSwitch } from './ThemeSwitch';

interface NavItem {
  label: string;
  to: string;
}

const primaryNav: NavItem[] = [
  { label: 'Live Market', to: '/live-market' },
  { label: 'Perps Data', to: '/perps-data' },
  { label: 'Market Trends', to: '/market-trends' },
  { label: 'Whale Flow', to: '/whale-flow' },
  { label: 'TATAF', to: '/tataf' },
];

const useUtcClock = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const stamp = now.toISOString().slice(0, 10) + ' · ' + now.toUTCString().slice(17, 25) + ' UTC';
  return stamp;
};

export const Masthead: React.FC = () => {
  const location = useLocation();
  const { signOut, user, loading } = useAuth();
  const { density, contrast, toggleDensity, toggleContrast } = useUiPreferences();
  const walletPref = useUserPreferences<string>('tataf.wallet_address', '');
  const hasWallet = Boolean(walletPref.value);
  const [menuOpen, setMenuOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const stamp = useUtcClock();
  const { activeTab, setActiveTab } = useTab();

  const isLandingPage = location.pathname === '/';
  const isCoinDetailsPage = location.pathname.startsWith('/coin/');
  const vaultMode = (location.state as { vaultMode?: boolean } | null)?.vaultMode;

  const effectiveNav = useMemo(() => {
    const nav = [...primaryNav];
    if (user && hasWallet) nav.push({ label: 'Portfolio', to: '/portfolio' });
    return nav;
  }, [user, hasWallet]);

  const secondaryNav = useMemo<NavItem[]>(() => {
    const items: NavItem[] = [];
    if (user) items.push({ label: 'Settings', to: '/settings' });
    items.push({ label: 'Help', to: '/help' });
    return items;
  }, [user]);

  const coinTabs = useMemo(
    () => [
      { id: 'overview', label: 'Overview' },
      { id: 'viewCharts', label: 'Charts' },
      { id: 'indicatorData', label: 'Indicators' },
      { id: 'aiAnalysis', label: 'Analysis' },
      { id: 'predictions', label: 'Predictions' },
    ],
    [],
  );

  const vaultTabs = useMemo(
    () => [
      { id: 'Liquidity Vault', label: 'Liquidity' },
      { id: 'A Vault', label: 'A Vault' },
      { id: 'B Vault', label: 'B Vault' },
      { id: 'Consensus Vault', label: 'Consensus' },
    ],
    [],
  );

  const showBottomDock = (isCoinDetailsPage || vaultMode) && !isLandingPage && !loading && !!user;
  const tabsToShow = isCoinDetailsPage ? coinTabs : vaultTabs;

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (showBottomDock) {
      document.body.dataset.coinDock = 'on';
    } else {
      delete document.body.dataset.coinDock;
    }
    return () => {
      delete document.body.dataset.coinDock;
    };
  }, [showBottomDock]);

  if (isLandingPage || loading || !user) {
    return null;
  }

  const navBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: '8px 14px',
    color: active ? 'var(--text)' : 'var(--text-2)',
    borderBottom: active ? '1px solid var(--signal)' : '1px solid transparent',
    fontFamily: 'var(--sans)',
    fontWeight: 500,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    fontSize: 11,
  });

  return (
    <>
      <header className="masthead">
        <div className="masthead-inner">
          <Link to="/perps-data" className="masthead-mark" aria-label="Crypn home">
            crypn<em>.</em>
          </Link>

          <nav
            className="m-hide"
            style={{
              display: 'flex',
              gap: 4,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {effectiveNav.map((item) => {
              const active =
                location.pathname === item.to ||
                (item.to !== '/' && location.pathname.startsWith(item.to));
              return (
                <Link key={item.label} to={item.to} style={navBtnStyle(active)}>
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <span className="masthead-slug" aria-label="Connection status" title="Live market feed">
              <span className="dot" />
              <span>LIVE</span>
            </span>
            <span style={{ color: 'var(--rule)' }} className="m-hide">
              │
            </span>
            <span className="mono m-hide" style={{ fontSize: 10.5, color: 'var(--text-2)' }}>
              {stamp}
            </span>
            <span style={{ color: 'var(--rule)' }} className="m-hide">
              │
            </span>

            <ThemeSwitch />

            <div style={{ position: 'relative' }} className="m-hide">
              <button
                type="button"
                className="btn"
                style={{ padding: '5px 9px', fontSize: 10.5 }}
                onClick={() => setPrefsOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={prefsOpen}
                aria-label="UI preferences"
              >
                UI ▾
              </button>
              {prefsOpen && (
                <div
                  role="menu"
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: 'calc(100% + 6px)',
                    zIndex: 100,
                    background: 'var(--ink-2)',
                    border: '1px solid var(--rule)',
                    padding: 14,
                    minWidth: 220,
                  }}
                >
                  <div className="tab-sm muted" style={{ marginBottom: 8 }}>
                    DENSITY
                  </div>
                  <div className="theme-switch" style={{ width: '100%', marginBottom: 14 }}>
                    {(['compact', 'comfortable'] as const).map((d) => (
                      <button
                        key={d}
                        type="button"
                        className={density === d ? 'on' : ''}
                        onClick={() => {
                          if (density !== d) toggleDensity();
                        }}
                        style={{ flex: 1 }}
                      >
                        {d.toUpperCase()}
                      </button>
                    ))}
                  </div>
                  <div className="tab-sm muted" style={{ marginBottom: 8 }}>
                    CONTRAST
                  </div>
                  <div className="theme-switch" style={{ width: '100%', marginBottom: 14 }}>
                    {(['standard', 'high'] as const).map((c) => (
                      <button
                        key={c}
                        type="button"
                        className={contrast === c ? 'on' : ''}
                        onClick={() => {
                          if (contrast !== c) toggleContrast();
                        }}
                        style={{ flex: 1 }}
                      >
                        {c === 'standard' ? 'NORMAL' : 'HIGH'}
                      </button>
                    ))}
                  </div>
                  <ShortcutHelpPopover />
                </div>
              )}
            </div>

            <nav
              className="m-hide"
              style={{ display: 'flex', gap: 0, alignItems: 'center' }}
              aria-label="Secondary"
            >
              {secondaryNav.map((item) => {
                const active = location.pathname.startsWith(item.to);
                return (
                  <Link
                    key={item.label}
                    to={item.to}
                    style={{
                      ...navBtnStyle(active),
                      padding: '6px 10px',
                      fontSize: 10.5,
                      letterSpacing: '0.06em',
                    }}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            <button
              type="button"
              className="btn m-hide"
              style={{ padding: '6px 12px', fontSize: 10.5 }}
              onClick={signOut}
              aria-label="Sign out"
            >
              Sign Out
            </button>

            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              className="btn md:hidden"
              style={{ padding: '6px 10px', fontSize: 10.5 }}
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
            >
              {menuOpen ? <X size={16} /> : <Menu size={16} />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div
            className="md:hidden"
            style={{
              borderTop: '1px solid var(--rule)',
              padding: '12px 16px',
              background: 'var(--ink)',
              display: 'grid',
              gap: 6,
            }}
          >
            {[...effectiveNav, ...secondaryNav].map((item) => {
              const active = location.pathname.startsWith(item.to);
              return (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setMenuOpen(false)}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid var(--rule)',
                    color: active ? 'var(--text)' : 'var(--text-2)',
                    background: active ? 'var(--ink-3)' : 'transparent',
                    fontSize: 12,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  {item.label}
                </Link>
              );
            })}
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                signOut();
              }}
              className="btn"
              style={{ justifyContent: 'center', marginTop: 4 }}
            >
              Sign Out
            </button>
            <div style={{ marginTop: 10 }}>
              <div className="tab-sm muted" style={{ marginBottom: 6 }}>
                THEME
              </div>
              <ThemeSwitch />
            </div>
            <div style={{ marginTop: 6 }}>
              <button
                type="button"
                onClick={toggleDensity}
                className="btn"
                style={{ width: '100%', justifyContent: 'center', fontSize: 10.5 }}
              >
                DENSITY · {density === 'compact' ? 'COMPACT' : 'COMFORTABLE'}
              </button>
            </div>
            <div>
              <button
                type="button"
                onClick={toggleContrast}
                className="btn"
                style={{ width: '100%', justifyContent: 'center', fontSize: 10.5 }}
              >
                CONTRAST · {contrast === 'high' ? 'HIGH' : 'NORMAL'}
              </button>
            </div>
          </div>
        )}
      </header>

      {showBottomDock && (
        <div className="coin-tab-dock">
          <div className="tabs" style={{ borderBottom: 0 }}>
            {tabsToShow.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                aria-pressed={activeTab === tab.id}
                className={`tab-item ${activeTab === tab.id ? 'active' : ''}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default Masthead;
